import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
  Alert,
  Collapse,
  InputAdornment,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiUrl } from '../config/api';

// Types
interface PurchaseInvoice {
  id: number;
  invoice_number: string;
  vendor_name: string;
  vendor_email?: string;
  vendor_phone?: string;
  vendor_address?: string;
  property: {
    id: number;
    title: string;
    currency?: {
      id: number;
      code: string;
      symbol: string;
    };
  };
  unit?: {
    id: number;
    unit_number: string;
    unit_name?: string;
  };
  currency: {
    id: number;
    code: string;
    symbol: string;
  };
  invoice_date: string;
  due_date?: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  paid_amount: number;
  remaining_amount: number;
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'cancelled';
  payment_status: 'unpaid' | 'partial' | 'paid';
  category: string;
  category_label: string;
  description?: string;
  notes?: string;
  attachments?: string[];
  payment_date?: string;
  payment_method?: string;
  payment_reference?: string;
  days_until_due?: number;
  is_overdue: boolean;
  status_badge_color: string;
  property_hierarchy_name?: string;
  creator: {
    id: number;
    name: string;
  };
  created_at: string;
  updated_at: string;
}

interface FilterOptions {
  categories: Record<string, string>;
  statuses: Record<string, string>;
  vendors: string[];
}

interface Property {
  id: number;
  title: string;
  currency?: {
    id: number;
    code: string;
    symbol: string;
  };
}

interface Unit {
  id: number;
  unit_number: string;
  unit_name?: string;
}

interface PurchaseInvoicesProps {
  selectedPropertyId?: number | null;
  selectedUnitId?: number | null;
  properties: Property[];
}

const PurchaseInvoices: React.FC<PurchaseInvoicesProps> = ({
  selectedPropertyId,
  selectedUnitId,
  properties,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // State
  const [showFilters, setShowFilters] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<PurchaseInvoice | null>(null);
  const [showViewDialog, setShowViewDialog] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    property_id: selectedPropertyId || '',
    unit_id: selectedUnitId || '',
    status: '',
    category: '',
    vendor: '',
    invoice_number: '',
    search: '',
    start_date: null as Dayjs | null,
    end_date: null as Dayjs | null,
  });

  // Pagination state
  const [page, setPage] = useState(1);
  const [perPage] = useState(15);

  // Update filters when selected property/unit changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      property_id: selectedPropertyId || '',
      unit_id: selectedUnitId || '',
    }));
  }, [selectedPropertyId, selectedUnitId]);

  // API calls
  const { data: invoicesData, isLoading: loadingInvoices, error: invoicesError } = useQuery({
    queryKey: ['purchase-invoices', filters, page, perPage],
    queryFn: async () => {
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          if (key === 'start_date' && value) {
            params.append(key, (value as Dayjs).format('YYYY-MM-DD'));
          } else if (key === 'end_date' && value) {
            params.append(key, (value as Dayjs).format('YYYY-MM-DD'));
          } else {
            params.append(key, value.toString());
          }
        }
      });
      
      params.append('page', page.toString());
      params.append('per_page', perPage.toString());

      const response = await fetch(`${getApiUrl('/accounting/purchase-invoices')}?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch purchase invoices');
      }

      const result = await response.json();
      return result.data;
    },
  });

  const { data: filterOptions } = useQuery({
    queryKey: ['purchase-invoices-filter-options', selectedPropertyId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedPropertyId) {
        params.append('property_id', selectedPropertyId.toString());
      }

      const response = await fetch(`${getApiUrl('/accounting/purchase-invoices-filter-options')}?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch filter options');
      }

      const result = await response.json();
      return result.data as FilterOptions;
    },
  });

  const { data: units } = useQuery({
    queryKey: ['units', selectedPropertyId],
    queryFn: async () => {
      if (!selectedPropertyId) return [];

      const response = await fetch(`${getApiUrl(`/properties/${selectedPropertyId}/units`)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch units');
      }

      const result = await response.json();
      return result.data as Unit[];
    },
    enabled: !!selectedPropertyId,
  });

  // Helper functions
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'default';
      case 'pending': return 'warning';
      case 'paid': return 'success';
      case 'overdue': return 'error';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (paymentStatus: string) => {
    switch (paymentStatus) {
      case 'unpaid': return 'error';
      case 'partial': return 'warning';
      case 'paid': return 'success';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number, currency?: { symbol: string; code: string }) => {
    return `${currency?.symbol || '$'} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('MMM DD, YYYY');
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setFilters({
      property_id: selectedPropertyId || '',
      unit_id: selectedUnitId || '',
      status: '',
      category: '',
      vendor: '',
      invoice_number: '',
      search: '',
      start_date: null,
      end_date: null,
    });
    setPage(1);
  };

  const handleViewInvoice = (invoice: PurchaseInvoice) => {
    setSelectedInvoice(invoice);
    setShowViewDialog(true);
  };

  const handlePrintInvoice = (invoice: PurchaseInvoice) => {
    // TODO: Implement print functionality
    console.log('Print invoice:', invoice);
  };

  const handleShareInvoice = (invoice: PurchaseInvoice) => {
    // TODO: Implement share functionality
    console.log('Share invoice:', invoice);
  };

  const handleEditInvoice = (invoice: PurchaseInvoice) => {
    // TODO: Implement edit functionality
    console.log('Edit invoice:', invoice);
  };

  const handleDeleteInvoice = (invoice: PurchaseInvoice) => {
    // TODO: Implement delete functionality
    console.log('Delete invoice:', invoice);
  };

  const handleMarkAsPaid = (invoice: PurchaseInvoice) => {
    // TODO: Implement mark as paid functionality
    console.log('Mark as paid:', invoice);
  };

  if (loadingInvoices) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (invoicesError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error loading purchase invoices: {(invoicesError as Error).message}
      </Alert>
    );
  }

  const invoices = invoicesData?.data || [];
  const totalPages = Math.ceil((invoicesData?.total || 0) / perPage);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Purchase Invoices
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ minWidth: 'auto' }}
            >
              Filters
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                // TODO: Implement add new invoice
                console.log('Add new invoice');
              }}
            >
              New Invoice
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Collapse in={showFilters}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                {/* Property Filter */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Property</InputLabel>
                    <Select
                      value={filters.property_id}
                      label="Property"
                      onChange={(e) => handleFilterChange('property_id', e.target.value)}
                    >
                      <MenuItem value="">All Properties</MenuItem>
                      {properties.map((property) => (
                        <MenuItem key={property.id} value={property.id}>
                          {property.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Unit Filter */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Unit</InputLabel>
                    <Select
                      value={filters.unit_id}
                      label="Unit"
                      onChange={(e) => handleFilterChange('unit_id', e.target.value)}
                      disabled={!selectedPropertyId}
                    >
                      <MenuItem value="">All Units</MenuItem>
                      {units?.map((unit) => (
                        <MenuItem key={unit.id} value={unit.id}>
                          {unit.unit_number} {unit.unit_name && `- ${unit.unit_name}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Status Filter */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      label="Status"
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      {filterOptions?.statuses && Object.entries(filterOptions.statuses).map(([key, label]) => (
                        <MenuItem key={key} value={key}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Category Filter */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={filters.category}
                      label="Category"
                      onChange={(e) => handleFilterChange('category', e.target.value)}
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {filterOptions?.categories && Object.entries(filterOptions.categories).map(([key, label]) => (
                        <MenuItem key={key} value={key}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Vendor Filter */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Vendor</InputLabel>
                    <Select
                      value={filters.vendor}
                      label="Vendor"
                      onChange={(e) => handleFilterChange('vendor', e.target.value)}
                    >
                      <MenuItem value="">All Vendors</MenuItem>
                      {filterOptions?.vendors?.map((vendor) => (
                        <MenuItem key={vendor} value={vendor}>
                          {vendor}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Invoice Number Filter */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Invoice Number"
                    value={filters.invoice_number}
                    onChange={(e) => handleFilterChange('invoice_number', e.target.value)}
                  />
                </Grid>

                {/* Date Range Filters */}
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="From Date"
                    value={filters.start_date}
                    onChange={(value) => handleFilterChange('start_date', value)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="To Date"
                    value={filters.end_date}
                    onChange={(value) => handleFilterChange('end_date', value)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>

                {/* Search Field */}
                <Grid item xs={12} md={9}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search invoices, vendor names, descriptions..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Clear Filters Button */}
                <Grid item xs={12} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={clearFilters}
                    size="small"
                  >
                    Clear Filters
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Collapse>

        {/* Invoice List */}
        <Card>
          <CardContent>
            {invoices.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="h6" color="text.secondary">
                  No purchase invoices found
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Try adjusting your filters or create a new invoice
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice #</TableCell>
                        <TableCell>Vendor</TableCell>
                        <TableCell sx={{ minWidth: '250px', maxWidth: '350px' }}>Property</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Due Date</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="right">Paid</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Payment</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.map((invoice: PurchaseInvoice) => (
                        <TableRow key={invoice.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {invoice.invoice_number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {invoice.vendor_name}
                            </Typography>
                            {invoice.vendor_email && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {invoice.vendor_email}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ minWidth: '250px', maxWidth: '350px' }}>
                            <Typography variant="body2">
                              {invoice.property_hierarchy_name || invoice.property.title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {invoice.category_label}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(invoice.invoice_date)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {invoice.due_date ? (
                              <Box>
                                <Typography variant="body2">
                                  {formatDate(invoice.due_date)}
                                </Typography>
                                {invoice.is_overdue && (
                                  <Typography variant="caption" color="error">
                                    Overdue
                                  </Typography>
                                )}
                                {invoice.days_until_due !== null && invoice.days_until_due !== undefined && invoice.days_until_due > 0 && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {invoice.days_until_due} days
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              {formatCurrency(invoice.total_amount, invoice.currency)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color={invoice.paid_amount > 0 ? 'success.main' : 'text.secondary'}>
                              {formatCurrency(invoice.paid_amount, invoice.currency)}
                            </Typography>
                            {invoice.remaining_amount > 0 && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Remaining: {formatCurrency(invoice.remaining_amount, invoice.currency)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                              color={getStatusColor(invoice.status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={invoice.payment_status.charAt(0).toUpperCase() + invoice.payment_status.slice(1)}
                              color={getPaymentStatusColor(invoice.payment_status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box display="flex" gap={0.5}>
                              <Tooltip title="View">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewInvoice(invoice)}
                                >
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Print">
                                <IconButton
                                  size="small"
                                  onClick={() => handlePrintInvoice(invoice)}
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Share">
                                <IconButton
                                  size="small"
                                  onClick={() => handleShareInvoice(invoice)}
                                >
                                  <ShareIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditInvoice(invoice)}
                                  disabled={invoice.status === 'paid'}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {invoice.payment_status !== 'paid' && (
                                <Tooltip title="Mark as Paid">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleMarkAsPaid(invoice)}
                                    color="success"
                                  >
                                    <PaymentIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteInvoice(invoice)}
                                  color="error"
                                  disabled={invoice.status === 'paid'}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(_, newPage) => setPage(newPage)}
                      color="primary"
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* View Invoice Dialog */}
        <Dialog
          open={showViewDialog}
          onClose={() => setShowViewDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Invoice Details - {selectedInvoice?.invoice_number}
          </DialogTitle>
          <DialogContent>
            {selectedInvoice && (
              <Box>
                {/* TODO: Implement detailed invoice view */}
                <Typography variant="h6" gutterBottom>
                  {selectedInvoice.vendor_name}
                </Typography>
                <Typography variant="body1">
                  Total: {formatCurrency(selectedInvoice.total_amount, selectedInvoice.currency)}
                </Typography>
                {/* Add more invoice details here */}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowViewDialog(false)}>
              Close
            </Button>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={() => selectedInvoice && handlePrintInvoice(selectedInvoice)}
            >
              Print
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default PurchaseInvoices; 